import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Search,
  Filter,
  Eye,
  UserPlus,
  Download,
  X,
  Loader2,
  ShieldCheck,
  Trash2,
  FileVideo,
} from "lucide-react";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Checkbox } from "@/projects/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/projects/components/ui/alert-dialog";
import { StatusBadge } from "@/projects/components/status-badge";
import {
  DataPagination,
  useServerPagination,
} from "@/projects/components/data-pagination";
import { VideoPlayerModal } from "@/projects/components/video-player-modal";
import { AssignTeacherModal } from "@/projects/admin/components/assign-teacher-modal";
import { SubmissionDetailsDrawer } from "@/projects/admin/components/submission-details-drawer";
import {
  bulkDeleteStudentVideoSubmissions,
  deleteStudentVideoSubmission,
  fetchStudentVideoSubmissions,
  fetchStudentVideoSubmissionsPage,
  fetchStudentVideoSubmissionsRawAll,
  getSubmissionApiErrorMessage,
  STATUS_API_VALUE,
  toApiDate,
  type ApiStudentVideoSubmission,
  type StudentVideoSubmissionFilters,
} from "@/projects/lib/submissionsApi";
import { fetchTeachers } from "@/projects/lib/teachersApi";
import { fetchTopics, type Topic } from "@/projects/lib/topicsApi";
import type {
  Submission,
  SubmissionStatus,
  Teacher,
} from "@/projects/lib/store";
import { toast } from "@/projects/lib/toast";

const STATUSES: (SubmissionStatus | "All")[] = [
  "All",
  "Pending",
  "Assigned for Verification",
  "Not Proper",
  "Ready for Reward",
];

function buildFilters(input: {
  q: string;
  status: SubmissionStatus | "All";
  teacherId: string;
  topicId: string;
  from: string;
  to: string;
}): StudentVideoSubmissionFilters {
  const filters: StudentVideoSubmissionFilters = {};
  if (input.from) {
    filters.from_date = toApiDate(input.from);
  }
  if (input.to) {
    filters.to_date = toApiDate(input.to);
  }
  if (input.status !== "All") {
    filters.status = STATUS_API_VALUE[input.status];
  }
  if (input.teacherId !== "all") {
    filters.assigned_teacher = input.teacherId;
  }
  if (input.topicId !== "all") {
    filters.topic = input.topicId;
  }
  if (input.q.trim()) {
    filters.search = input.q.trim();
  }
  return filters;
}

export function SubmissionsPage() {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState<SubmissionStatus | "All">("All");
  const [teacherId, setTeacherId] = useState<string>("all");
  const [topicId, setTopicId] = useState<string>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selectedMap, setSelectedMap] = useState<Record<string, Submission>>({});
  const [videoOpen, setVideoOpen] = useState<Submission | null>(null);
  const [assignFor, setAssignFor] = useState<string[] | null>(null);
  const [detail, setDetail] = useState<Submission | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Submission | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchTeachers();
        if (!cancelled) {
          setTeachers(rows);
        }
      } catch {
        if (!cancelled) {
          setTeachers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchTopics();
        if (!cancelled) {
          setTopics(rows);
        }
      } catch {
        if (!cancelled) {
          setTopics([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeFilters = buildFilters({
    q: debouncedQ,
    status,
    teacherId,
    topicId,
    from,
    to,
  });

  const pagination = useServerPagination(
    ({ page, pageSize }) =>
      fetchStudentVideoSubmissionsPage(activeFilters, page, pageSize),
    [debouncedQ, status, teacherId, topicId, from, to],
    {
      onError: (error: unknown) => {
        const err = error as {
          response?: { data?: { message?: string; detail?: string } };
          message?: string;
        };
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.detail ||
            err?.message ||
            "Failed to load video submissions."
        );
      },
    }
  );
  const submissions = pagination.items;
  const loading = pagination.loading;

  useEffect(() => {
    setSelectedMap({});
  }, [debouncedQ, status, teacherId, topicId, from, to]);

  const selectedIds = Object.keys(selectedMap);
  const selectedCount = selectedIds.length;

  const assignSubs = assignFor
    ? assignFor
        .map(id => selectedMap[id] ?? submissions.find(s => s.id === id))
        .filter((s): s is Submission => !!s)
    : null;

  const pageItems = submissions;
  const totalCount = pagination.meta.count;
  const allFilteredSelected =
    totalCount > 0 && selectedCount >= totalCount;
  const someSelected = selectedCount > 0 && !allFilteredSelected;

  const toggleSelection = (submission: Submission, checked: boolean) => {
    setSelectedMap(prev => {
      const next = { ...prev };
      if (checked) {
        next[submission.id] = submission;
      } else {
        delete next[submission.id];
      }
      return next;
    });
  };

  const toggleAll = async () => {
    if (allFilteredSelected) {
      setSelectedMap({});
      return;
    }

    setSelectingAll(true);
    try {
      const all = await fetchStudentVideoSubmissions(activeFilters);
      if (!all.length) {
        toast.error("No records to select.");
        return;
      }
      const next: Record<string, Submission> = {};
      all.forEach(s => {
        next[s.id] = s;
      });
      setSelectedMap(next);
    } catch (error) {
      toast.error(
        getSubmissionApiErrorMessage(error, "Failed to select all submissions.")
      );
    } finally {
      setSelectingAll(false);
    }
  };

  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (q) {
    chips.push({ key: "q", label: `Search: ${q}`, clear: () => setQ("") });
  }
  if (status !== "All") {
    chips.push({
      key: "s",
      label: `Status: ${status}`,
      clear: () => setStatus("All"),
    });
  }
  if (teacherId !== "all") {
    const t = teachers.find(x => x.userId === teacherId || x.id === teacherId);
    chips.push({
      key: "t",
      label: `Teacher: ${t?.name ?? teacherId}`,
      clear: () => setTeacherId("all"),
    });
  }
  if (topicId !== "all") {
    const tp = topics.find(x => x.id === topicId);
    chips.push({
      key: "tp",
      label: `Topic: ${tp?.name ?? topicId}`,
      clear: () => setTopicId("all"),
    });
  }
  if (from) {
    chips.push({
      key: "f",
      label: `From ${toApiDate(from)}`,
      clear: () => setFrom(""),
    });
  }
  if (to) {
    chips.push({
      key: "tt",
      label: `To ${toApiDate(to)}`,
      clear: () => setTo(""),
    });
  }

  const escapeCsvValue = (value: unknown) => {
    const text = value === null || value === undefined ? "" : String(value);
    const escaped = text.replace(/"/g, '""');
    if (/[",\n\r]/.test(escaped)) {
      return `"${escaped}"`;
    }
    return escaped;
  };

  const formatRawValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const handleExport = async (scope: "all" | "filtered" | "selected") => {
    let rows: ApiStudentVideoSubmission[];
    const exportFilters = scope === "all" ? undefined : activeFilters;
    try {
      rows = await fetchStudentVideoSubmissionsRawAll(exportFilters);
    } catch (error) {
      toast.error(
        (error as { message?: string })?.message ||
          "Failed to export submissions."
      );
      return;
    }

    if (scope === "selected") {
      rows = rows.filter(
        row =>
          selectedIds.includes(row.reference ?? String(row.id)) ||
          selectedIds.includes(String(row.id))
      );
    }

    if (!rows.length) {
      toast.error("No records to export.");
      return;
    }

    const excludedFields = new Set([
      "video_file_size",
      "original_file_size",
      "compressed_file_size",
      "was_compressed",
      "upload_error",
      "assigned_teacher",
      "id",
    ]);

    const headers: string[] = [];
    const seenHeaders = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (!seenHeaders.has(key) && !excludedFields.has(key)) {
          seenHeaders.add(key);
          headers.push(key);
        }
      }
    }

    const csv = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map(row =>
        headers
          .map(key => escapeCsvValue(formatRawValue(row[key])))
          .join(",")
      ),
    ].join("\r\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submissions-${scope}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export completed successfully");
  };

  const resetFilters = () => {
    setQ("");
    setDebouncedQ("");
    setStatus("All");
    setTeacherId("all");
    setTopicId("all");
    setFrom("");
    setTo("");
  };

  const resolveSelectedApiIds = (): (string | number)[] =>
    selectedIds
      .map(id => selectedMap[id]?.apiId ?? id)
      .filter(Boolean);

  const handleBulkDelete = async () => {
    const ids = resolveSelectedApiIds();
    if (!ids.length) {
      toast.error("No valid submissions selected for deletion.");
      return;
    }
    setBulkDeleting(true);
    try {
      await bulkDeleteStudentVideoSubmissions(ids);
      toast.success(
        `${ids.length} submission${ids.length === 1 ? "" : "s"} deleted`
      );
      setConfirmBulkDelete(false);
      setSelectedMap({});
      pagination.reload();
    } catch (error) {
      toast.error(
        getSubmissionApiErrorMessage(error, "Failed to delete submissions.")
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search name, ID, title, reference…"
              className="w-64 pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(v => !v)}
          >
            <Filter className="mr-2 h-4 w-4" /> Filters
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => pagination.reload()}
            disabled={loading}
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <>
              <Button size="sm" onClick={() => setAssignFor(selectedIds)}>
                <UserPlus className="mr-2 h-4 w-4" /> Bulk Assign (
                {selectedCount})
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setConfirmBulkDelete(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Bulk Delete (
                {selectedCount})
              </Button>
            </>
          )}
          <div className="relative">
            <Select
              onValueChange={v =>
                void handleExport(v as "all" | "filtered" | "selected")
              }
            >
              <SelectTrigger className="w-[170px]">
                <Download className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Export to Excel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="filtered">Filtered records</SelectItem>
                <SelectItem value="selected" disabled={selectedCount === 0}>
                  Selected ({selectedCount})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="bg-card shadow-soft rounded-xl border p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div>
              <Label className="text-xs">Status</Label>
              <Select
                value={status}
                onValueChange={v => setStatus(v as SubmissionStatus | "All")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Assigned Teacher</Label>
              <Select value={teacherId} onValueChange={setTeacherId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teachers</SelectItem>
                  {teachers.map(t => (
                    <SelectItem key={t.id} value={t.userId || t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Topic</Label>
              <Select value={topicId} onValueChange={setTopicId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All topics</SelectItem>
                  {topics.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From Date</Label>
              <Input
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">To Date</Label>
              <Input
                type="date"
                value={to}
                onChange={e => setTo(e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={resetFilters}
              >
                Reset
              </Button>
            </div>
          </div>
        </div>
      )}

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map(c => (
            <button
              key={c.key}
              onClick={c.clear}
              className="bg-muted hover:bg-muted/70 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs"
            >
              {c.label} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      <div className="bg-card shadow-soft overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-10">
                  {selectingAll ? (
                    <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                  ) : (
                    <Checkbox
                      checked={
                        allFilteredSelected
                          ? true
                          : someSelected
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={() => void toggleAll()}
                      disabled={loading || totalCount === 0}
                      title={
                        allFilteredSelected
                          ? "Deselect all"
                          : `Select all ${totalCount} matching records`
                      }
                    />
                  )}
                </TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Date of Birth</TableHead>
                <TableHead>Video Title</TableHead>
                <TableHead>Topic</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[1%] text-right whitespace-nowrap">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} className="py-16 text-center">
                    <div className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading submissions…
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && submissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-16 text-center">
                    <div className="mx-auto max-w-sm">
                      <div className="text-sm font-medium">
                        No records match the selected filters.
                      </div>
                      <div className="text-muted-foreground text-xs">
                        Try adjusting or resetting your filters.
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                pageItems.map(s => (
                  <TableRow
                    key={s.id}
                    className="hover:bg-muted/40 cursor-pointer"
                    onClick={() => setDetail(s)}
                  >
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={s.id in selectedMap}
                        onCheckedChange={v => toggleSelection(s, !!v)}
                      />
                    </TableCell>
                    <TableCell className="text-royal font-mono text-xs font-medium">
                      {s.id}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{s.studentName}</div>
                      <div className="text-muted-foreground text-xs">
                        {s.studentId}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.birthDate ? (
                        new Date(s.birthDate).toLocaleDateString()
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <div className="truncate font-medium">{s.videoTitle}</div>
                      <div className="text-muted-foreground text-xs">
                        {s.fileSizeMB} MB · {s.fileName}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[160px] text-xs">
                      {s.topicName ? (
                        <span className="truncate">{s.topicName}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {new Date(s.submittedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.assignedTeacherName ? (
                        <>
                          <div className="text-foreground font-medium">
                            {s.assignedTeacherName}
                          </div>
                          <div className="text-muted-foreground">
                            {s.assignedAt &&
                              new Date(s.assignedAt).toLocaleDateString()}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={s.status} />
                    </TableCell>
                    <TableCell
                      className="w-[1%] text-right whitespace-nowrap"
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="w-[70px] justify-center px-2"
                          onClick={() => setVideoOpen(s)}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" /> View
                        </Button>
                        <Link
                          to="/admin/verify/$id"
                          params={{ id: s.apiId || s.id }}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-[80px] justify-center px-2"
                          >
                            <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Verify
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-[92px] justify-center px-2"
                          onClick={() => setAssignFor([s.id])}
                        >
                          {s.assignedTeacherId ? "Reassign" : "Assign"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive w-[60px] justify-center px-1"
                          onClick={() => setConfirmDelete(s)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        {!loading && submissions.length > 0 && (
          <DataPagination pagination={pagination} itemLabel="submissions" />
        )}
      </div>

      <VideoPlayerModal
        submission={videoOpen}
        open={!!videoOpen}
        onOpenChange={o => !o && setVideoOpen(null)}
      />
      <AssignTeacherModal
        submissions={assignSubs}
        open={!!assignFor}
        onClose={() => {
          setAssignFor(null);
          setSelectedMap({});
        }}
        onAssigned={() => {
          pagination.reload();
        }}
      />
      <SubmissionDetailsDrawer
        submission={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
      />

      <AlertDialog
        open={confirmBulkDelete}
        onOpenChange={o => !o && !bulkDeleting && setConfirmBulkDelete(false)}
      >
        <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedCount} submission
              {selectedCount === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected video submissions across
              all pages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async e => {
                e.preventDefault();
                await handleBulkDelete();
              }}
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                `Delete ${selectedCount}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={o => !o && !deleting && setConfirmDelete(null)}
      >
        <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md overflow-hidden">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the video below. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {confirmDelete && (
            <div className="bg-muted/40 max-w-full min-w-0 overflow-hidden rounded-lg border p-3 text-sm">
              <div className="flex min-w-0 items-start gap-3">
                <div className="bg-background text-muted-foreground grid h-10 w-10 shrink-0 place-items-center rounded-md border">
                  <FileVideo className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 space-y-1.5 overflow-hidden">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="text-royal font-mono text-sm font-semibold">
                      {confirmDelete.id}
                    </span>
                    <StatusBadge status={confirmDelete.status} />
                  </div>
                  <div className="text-foreground line-clamp-2 leading-snug font-medium break-words">
                    {confirmDelete.videoTitle || "Untitled video"}
                  </div>
                  <div className="text-muted-foreground text-xs break-all">
                    <span className="line-clamp-2">
                      {confirmDelete.fileName || "—"}
                    </span>
                    {confirmDelete.fileSizeMB != null && (
                      <span className="whitespace-nowrap">
                        {" "}
                        · {confirmDelete.fileSizeMB} MB
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid min-w-0 grid-cols-2 gap-x-3 gap-y-2 border-t pt-3 text-xs">
                <div className="min-w-0 overflow-hidden">
                  <div className="text-muted-foreground">Student</div>
                  <div className="text-foreground truncate font-medium">
                    {confirmDelete.studentName || "—"}
                  </div>
                  {confirmDelete.studentId && (
                    <div className="text-muted-foreground truncate">
                      ID: {confirmDelete.studentId}
                    </div>
                  )}
                </div>
                <div className="min-w-0 overflow-hidden">
                  <div className="text-muted-foreground">Topic</div>
                  <div className="text-foreground truncate font-medium">
                    {confirmDelete.topicName || "—"}
                  </div>
                </div>
                <div className="min-w-0 overflow-hidden">
                  <div className="text-muted-foreground">Submitted</div>
                  <div className="text-foreground font-medium">
                    {new Date(confirmDelete.submittedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="min-w-0 overflow-hidden">
                  <div className="text-muted-foreground">Assigned</div>
                  <div className="text-foreground truncate font-medium">
                    {confirmDelete.assignedTeacherName || "—"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async e => {
                e.preventDefault();
                if (!confirmDelete) {
                  return;
                }
                const id = confirmDelete.apiId || confirmDelete.id;
                setDeleting(true);
                try {
                  await deleteStudentVideoSubmission(id);
                  toast.success("Submission deleted");
                  setConfirmDelete(null);
                  setSelectedMap(prev => {
                    const next = { ...prev };
                    delete next[confirmDelete.id];
                    return next;
                  });
                  pagination.reload();
                } catch (error) {
                  toast.error(
                    getSubmissionApiErrorMessage(
                      error,
                      "Failed to delete submission."
                    )
                  );
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
